import forEach from "lodash/forEach";
import {
  CustomPaymentType,
  PaymentOption,
  PaymentOptionType,
} from "~features/checkout/common/checkout.types";
import { GetTotalsReturn } from "~features/checkout/legacy/helpers/getTotals";
import {
  IntegrationsQuery,
  IntegrationType,
  PaymentProvider,
  PosPaymentOptions,
  PaymentOption as BackendPaymentOption,
} from "~graphql/sdk";
import { UserRoles } from "~context";
import { usePaymentOptionsStore } from "./paymentOptions.store";

const getPOSPaymentOptions = (paymentOptions: PosPaymentOptions) => {
  const options: PaymentOption[] = [];

  forEach(paymentOptions, (paymentOption, key) => {
    if (paymentOption !== null) {
      if (key === "cash") {
        options.push({
          value: PaymentProvider.Cash,
          type: PaymentProvider.Cash,
          label: "Cash",
          id: PaymentProvider.Cash,
        });
      }

      if (key === "eftpos") {
        options.push({
          value: PaymentProvider.Eftpos,
          type: PaymentProvider.Eftpos,
          label: "Eftpos",
          id: PaymentProvider.Eftpos,
        });
      }

      if (key === "cc") {
        options.push({
          value: PaymentProvider.CreditCard,
          type: PaymentProvider.CreditCard,
          label: "Credit Card",
          id: PaymentProvider.CreditCard,
        });
      }
      if (key === "MPAiSA") {
        options.push({
          value: PaymentProvider.MpAiSa,
          type: PaymentProvider.MpAiSa,
          label: "MPAiSA",
          id: PaymentProvider.MpAiSa,
        });
      }
    }
  });
  return options;
};

// HACK: temp until we add ordering of payment integrations
const mapPaymentOptions = (paymentOptions: PaymentOption[]) => {
  const terminal = paymentOptions.find((p) => p.id.includes("-terminal"));
  paymentOptions = paymentOptions.filter((p) => !p.id.includes("-terminal"));

  const hasPaymentPlans = paymentOptions.some(
    (option) => option.paymentPlanMetadata
  );

  // If there are multiple card payment options, remove stripe pay in full
  // this supports scenarios where promoters prefer to use a specific gateway for pay in full (usually due to lower fees)
  const hasMultipleCardPaymentOptions =
    paymentOptions.filter(
      (option) =>
        (option.type === IntegrationType.PaymentStripe &&
          !option.bnplEnabled &&
          !option.paymentPlanMetadata) ||
        option.type === IntegrationType.PaymentWindcave ||
        option.type === IntegrationType.PaymentPin ||
        option.type === IntegrationType.PaymentMpaisa
    ).length > 1;

  if (hasPaymentPlans && hasMultipleCardPaymentOptions) {
    paymentOptions = paymentOptions.filter(
      (option) =>
        !(
          option.type === IntegrationType.PaymentStripe && !option.bnplEnabled
        ) || option.paymentPlanMetadata
    );
  }

  for (const paymentOption of paymentOptions) {
    if (paymentOption.order) {
      continue;
    }

    switch (paymentOption.type) {
      case CustomPaymentType.Credit:
        paymentOption.order = 0;
        break;
      case CustomPaymentType.Point:
        paymentOption.order = 0.5;
        break;
      case "on-hold":
        paymentOption.order = 0.6;
        break;

      case IntegrationType.PaymentStripe:
        if (!paymentOption.bnplEnabled) {
          if (
            paymentOptions.filter(
              (p) =>
                p.type === IntegrationType.PaymentStripe &&
                !p.paymentPlanMetadata &&
                !p.bnplEnabled
            ).length === 1
          ) {
            paymentOption.label = hasPaymentPlans
              ? "Pay in full"
              : "Credit or debit card";
          }
          paymentOption.order = 2;
        }

        break;
      case IntegrationType.PaymentWindcave:
        paymentOption.order = 3;
        break;
      case IntegrationType.PaymentMpaisa:
        paymentOption.order = 4;
        break;
      case IntegrationType.PaymentPin:
        paymentOption.order = 5;
        break;
      case IntegrationType.PaymentLaybuy:
        paymentOption.order = 6;
        break;
      case IntegrationType.PaymentAfterpay:
        paymentOption.order = 7.5;
        break;
      case CustomPaymentType.Paid:
        paymentOption.order = 8;
        break;

      case CustomPaymentType.Comp:
        paymentOption.order = 10;
        break;
      default:
        paymentOption.order = 200;
        break;
    }

    if (paymentOption.id.includes("-afterpay")) {
      paymentOption.order = 7;
    }
  }

  // Temp ...
  if (terminal) {
    terminal.order = 11;
    paymentOptions.push(terminal);
  }

  return paymentOptions.sort((a, b) => (a.order > b.order ? 1 : -1));
};

export interface GetPaymentOptionsProps {
  isAdmin: boolean;
  gateways: IntegrationsQuery["integrations"];
  totals: GetTotalsReturn;
  pointName: string;
  isPOSUser: boolean;
  posPaymentOptions: PosPaymentOptions;
  canSpendPoints: boolean;
  canCreateHoldOrder: boolean;
  hasPaidInFullWithCredits: boolean;
  isBackOfficeOrder: boolean;
  userRoles: UserRoles;
  backendPaymentOptions: BackendPaymentOption[];
}

export const getPaymentOptions = ({
  isAdmin,
  userRoles,
  isPOSUser,
  posPaymentOptions,
  totals,
  gateways,
  pointName,
  canSpendPoints,
  canCreateHoldOrder,
  hasPaidInFullWithCredits,
  isBackOfficeOrder,
  backendPaymentOptions,
}: GetPaymentOptionsProps): PaymentOption[] => {
  const gatewayPaymentOptions: PaymentOption[] = gateways.map((gateway) => ({
    id: gateway.id,
    value: gateway.name,
    type: gateway.type,
    paymentMethodType: gateway.paymentMethodType,
    label: gateway.name,
    transactionFee: gateway.transactionFee,
    transactionFeeType: gateway.transactionFeeType,
    transactionFee2: gateway.transactionFee2,
    transactionFeeType2: gateway.transactionFeeType2,
    bnplEnabled: gateway.bnplEnabled,
  }));

  const optionCompleteOrder: PaymentOption = {
    id: "paid",
    value: CustomPaymentType.Paid,
    type: CustomPaymentType.Paid,
    label: "Mark as paid",
    description:
      "Complete this order offline and mark as paid by either cash, eftpos, invoice or other means.",
  };

  const optionCompOrder: PaymentOption = {
    id: CustomPaymentType.Comp,
    value: CustomPaymentType.Comp,
    type: CustomPaymentType.Comp,
    label: "Complimentary",
    description: "Issue free tickets to special guests",
  };

  const optionHoldOrder: PaymentOption = {
    id: "on-hold",
    value: CustomPaymentType.OnHold,
    type: CustomPaymentType.OnHold,
    label: isAdmin
      ? `Create hold order${totals.orderTotal > 0 ? " (unpaid)" : ""}`
      : "Invoiced (hold order)",
  };

  const pointsTitlePrefix =
    totals.userPoints < totals.orderTotal ? "Partial payment" : "Pay";

  const optionPointsOrder: PaymentOption = {
    id: "point",
    value: CustomPaymentType.Point,
    type: CustomPaymentType.Point,
    label: `${pointsTitlePrefix} with ${pointName}`,
    description: `Available balance ${totals.userPoints}`,
  };

  const paymentOptions: PaymentOption[] = (() => {
    if (hasPaidInFullWithCredits) {
      return [];
    }

    const payments: PaymentOption[] = [];

    if (isBackOfficeOrder && !userRoles.Sales && !userRoles.SalesOutlet) {
      payments.push(optionCompOrder);
    }

    if (totals.orderTotal > 0 && !isPOSUser) {
      payments.push(...gatewayPaymentOptions);
      payments.push(
        ...(backendPaymentOptions?.map((option, idx): PaymentOption => {
          return {
            ...option,
            order: idx + 1,
            type: option.type as PaymentOptionType,
          };
        }) ?? [])
      );
    }

    if (canSpendPoints && totals.userPoints > 0) {
      payments.push(optionPointsOrder);
    }

    if (isPOSUser && posPaymentOptions) {
      payments.push(...getPOSPaymentOptions(posPaymentOptions));
    }

    if (canCreateHoldOrder) {
      payments.push(optionHoldOrder);
    }

    if (isBackOfficeOrder && totals.orderTotal >= 0) {
      payments.push(optionCompleteOrder);
    }

    const mapped = mapPaymentOptions(payments);

    usePaymentOptionsStore.setState({
      paymentOptions: mapped,
    });

    return mapped;
  })();

  return paymentOptions;
};
